import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { maybe, rd } from '@passionware/monads';
import { myRouting } from 'v5/features/core/routing';
import { myServices } from 'v5/services/services.connected.app';
import { adaptLegacyWorkspaceRoutingForLinks } from './adaptLegacyWorkspaceRoutingForLinks';
import { appScopeUtils } from './appScope.types';
import { useTeamRouting, useWorkspaceRouting } from './useScopedRouting';

/**
 * ## Legacy unscoped + enterprise scoped coexistence
 *
 * Three independent layers — do not mix them:
 *
 * 1. **`enterprise` feature flag** — sidebar / shell navigation only (workspace switcher,
 *    links into `/w/...` routes). Do not use the flag for list filters, entity links, or APIs.
 *
 * 2. **Browsing scope (this module)** — once the URL is explicit, **all in-page chrome**
 *    follows it: list filters, breadcrumbs, tabs, section links, list roots, back links,
 *    create defaults. On `/w/{ws}/...` use enterprise path builders; on legacy
 *    `/app/{org}/...` use legacy builders — regardless of the flag.
 *
 * 3. **Entity home** — use {@link entityWorkspaceRouting} only for a foreign entity
 *    (its home is not the browsing scope: it is wider, or a by-id reference) and
 *    public / share URLs. Do not re-apply entity workspace/team to rebuild tabs or
 *    breadcrumbs while already on a detail URL.
 *
 * @see entityWorkspaceRouting
 * @see .agents/rules/workspace-scoping-layers.md
 */

/**
 * Browsing-chrome route builders for the **current URL scope**
 * (breadcrumbs, tabs, section links, list roots, back links).
 * Reads the URL: inside a modal or drawer use `useBrowsingWorkspaceRoutes` (the opener's
 * scope); for a foreign entity, {@link entityWorkspaceRouting}.
 */
export function useAppWorkspaceRouting() {
  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const scope = rd.tryGet(rd.useLastWithPlaceholder(scopeRd));
  const enterpriseRouting = useWorkspaceRouting();
  const { pathname } = useLocation();

  return useMemo(() => {
    if (appScopeUtils.isEnterpriseScopedPath(pathname)) {
      return enterpriseRouting;
    }
    return adaptLegacyWorkspaceRoutingForLinks(
      myRouting.forWorkspace(scope?.organizationId ?? '')
    );
  }, [enterpriseRouting, scope?.organizationId, pathname]);
}

/**
 * Team-scoped browsing chrome on enterprise URLs; legacy workspace routing otherwise.
 */
export function useAppTeamRouting() {
  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const scope = rd.tryGet(rd.useLastWithPlaceholder(scopeRd));
  const enterpriseTeamRouting = useTeamRouting();
  const { pathname } = useLocation();

  if (appScopeUtils.isEnterpriseScopedPath(pathname)) {
    return enterpriseTeamRouting;
  }
  return myRouting.forWorkspace(scope?.organizationId ?? '');
}

/**
 * Callsheets paths — team-scoped on enterprise URLs, workspace-scoped on legacy URLs.
 */
export function useAppCallsheetsRouting() {
  const teamRouting = useTeamRouting();
  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const scope = rd.tryGet(rd.useLastWithPlaceholder(scopeRd));
  const { pathname } = useLocation();

  const useEnterpriseTeamPaths = appScopeUtils.isEnterpriseScopedPath(pathname);

  return useMemo(
    () => ({
      root: () =>
        useEnterpriseTeamPaths
          ? teamRouting.callsheets.root()
          : myRouting
              .forWorkspace(scope?.organizationId ?? '')
              .callsheets()
              .root(),
      detail: (id: string | number) =>
        useEnterpriseTeamPaths
          ? teamRouting.callsheets.detail(id)
          : myRouting
              .forWorkspace(scope?.organizationId ?? '')
              .callsheets()
              .detail(id)
    }),
    [useEnterpriseTeamPaths, teamRouting, scope?.organizationId]
  );
}

/**
 * Shortlists list chrome — team-scoped on enterprise URLs, legacy otherwise.
 * For links to a specific shortlist, use {@link getShortlistDetailPath} (entity scope).
 */
export function useAppShortlistsRouting() {
  const teamRouting = useTeamRouting();
  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const scope = rd.tryGet(rd.useLastWithPlaceholder(scopeRd));
  const { pathname } = useLocation();

  const useEnterpriseTeamPaths = appScopeUtils.isEnterpriseScopedPath(pathname);

  return useMemo(
    () => ({
      root: () =>
        useEnterpriseTeamPaths
          ? teamRouting.shortlists.root()
          : myRouting
              .forWorkspace(scope?.organizationId ?? '')
              .shortlists()
              .root()
    }),
    [useEnterpriseTeamPaths, teamRouting, scope?.organizationId]
  );
}

/**
 * Productions paths — team-scoped on enterprise URLs, legacy otherwise
 * (same shape as callsheets; no workspace-without-team home).
 */
export function useAppProductionsRouting() {
  const teamRouting = useTeamRouting();
  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const scope = rd.tryGet(rd.useLastWithPlaceholder(scopeRd));
  const { pathname } = useLocation();

  const useEnterpriseTeamPaths = appScopeUtils.isEnterpriseScopedPath(pathname);

  return useMemo(() => {
    const productions = useEnterpriseTeamPaths
      ? teamRouting.productions
      : myRouting.forWorkspace(scope?.organizationId ?? '').productions();

    return {
      root: () => productions.root(),
      type: (productionTypeId: string | number) =>
        productions.type(productionTypeId),
      detail: (id: string | number) => productions.detail(id),
      resourcing: (productionId: string | number = ':id') =>
        productions.resourcing(productionId)
    };
  }, [useEnterpriseTeamPaths, teamRouting, scope?.organizationId]);
}

export function useIsEnterpriseEnabled(): boolean {
  const scope = maybe.map(
    myServices.appScopeService.useMaybeOrganizationId(),
    organizationId =>
      appScopeUtils.identityFromEntityWorkspace(organizationId, null)
  );
  return rd.getOrElse(
    myServices.featureService.useFeature(scope, 'enterprise'),
    false
  );
}
